import { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { 
  Folder,
  FileText,
  Video,
  Image,
  Music,
  Download,
  Play,
  Lock,
  Loader2,
  Search,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'course_module', label: 'Course Modules', icon: Folder },
  { value: 'stationery', label: 'Stationery', icon: FileText },
  { value: 'guide', label: 'Guides', icon: FileText },
  { value: 'template', label: 'Templates', icon: FileText },
  { value: 'bonus', label: 'Bonus Content', icon: FileText },
  { value: 'other', label: 'Other', icon: Folder }
];

const FILE_ICONS = {
  pdf: FileText,
  video: Video,
  image: Image,
  audio: Music
};

const TIER_LABELS = {
  free: 'Free',
  pen_friend: 'Pen Friend',
  correspondent: 'Correspondent',
  postmaster: 'Postmaster'
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Video Player Modal
const VideoPlayerModal = ({ resource, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl">
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-stone-300"
        >
          <X className="w-6 h-6" />
        </button>
        <video
          src={`${API_URL}/api/resources/stream/${resource.id}`}
          controls
          autoPlay
          crossOrigin="use-credentials"
          className="w-full rounded-lg"
        >
          Your browser does not support video playback.
        </video>
        <p className="text-white text-center mt-4">{resource.title}</p>
      </div>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onPlay }) => {
  const FileIcon = FILE_ICONS[resource.file_type] || FileText;
  const isPlayable = resource.file_type === 'video' || resource.file_type === 'audio';

  const handleDownload = () => {
    window.open(`${API_URL}/api/resources/download/${resource.id}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-stone-200 p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          resource.file_type === 'video' ? 'bg-blue-100 text-blue-600' :
          resource.file_type === 'image' ? 'bg-pink-100 text-pink-600' :
          resource.file_type === 'audio' ? 'bg-purple-100 text-purple-600' :
          'bg-stone-100 text-stone-600'
        }`}>
          <FileIcon className="w-6 h-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-stone-800 truncate">{resource.title}</h3>
            {resource.module_number && (
              <span className="text-xs bg-lws-green/10 text-lws-green px-2 py-0.5 rounded">
                Module {resource.module_number}
              </span>
            )}
          </div>
          
          {resource.description && (
            <p className="text-sm text-stone-500 line-clamp-2 mb-2">{resource.description}</p>
          )}
          
          <div className="flex items-center gap-3 text-xs text-stone-400">
            <span className="capitalize">{resource.file_type}</span>
            <span>•</span>
            <span>{formatFileSize(resource.file_size)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isPlayable && (
            <button
              onClick={() => onPlay(resource)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Play"
            >
              <Play className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={handleDownload}
            className="p-2 text-stone-500 hover:text-lws-green hover:bg-stone-50 rounded-lg transition-colors"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function ResourceLibraryPage() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [playingVideo, setPlayingVideo] = useState(null);

  const fetchResources = useCallback(async () => {
    if (!user) return;
    
    try {
      const url = selectedCategory === 'all' 
        ? `${API_URL}/api/resources/list`
        : `${API_URL}/api/resources/list?category=${selectedCategory}`;
      
      const response = await fetch(url, { credentials: 'include' });
      const data = await response.json();
      setResources(data.resources || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedCategory]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Filter by search term
  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.description && r.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Group by category
  const groupedResources = filteredResources.reduce((acc, resource) => {
    const cat = resource.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(resource);
    return acc;
  }, {});

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <Lock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-serif text-stone-600">Members Only</h1>
          <p className="text-stone-400 mt-2">Please log in to access the resource library.</p>
          <a href="#login" className="mt-4 inline-block text-lws-green hover:underline">
            Log In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="resource-library-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-lws-cream to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-lws-gold font-medium mb-4">
              Member Resources
            </p>
            <h1 className="text-4xl font-serif text-lws-navy mb-4">Resource Library</h1>
            <p className="text-lg text-lws-taupe">
              Download course materials, stationery templates, guides, and bonus content.
            </p>
            <p className="text-sm text-lws-green mt-2">
              Your tier: <span className="font-medium">{TIER_LABELS[user.membership_tier] || 'Free'}</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 px-6 border-b border-stone-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-lws-green focus:border-transparent"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-lws-green text-white' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                All
              </button>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === cat.value 
                      ? 'bg-lws-green text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
              <Folder className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-stone-600">No resources available</h3>
              <p className="text-stone-400 mt-1">
                {selectedCategory === 'all' 
                  ? 'Resources will appear here once uploaded by admin.'
                  : `No ${CATEGORIES.find(c => c.value === selectedCategory)?.label || selectedCategory} available.`}
              </p>
              {!user.membership_tier && (
                <a href="#membership" className="mt-4 inline-block text-lws-green hover:underline">
                  Upgrade membership to unlock more resources
                </a>
              )}
            </div>
          ) : selectedCategory === 'all' ? (
            // Show grouped by category
            <div className="space-y-8">
              {Object.entries(groupedResources).map(([category, categoryResources]) => {
                const catInfo = CATEGORIES.find(c => c.value === category);
                return (
                  <div key={category}>
                    <h2 className="text-lg font-serif text-stone-800 mb-4 flex items-center gap-2">
                      {catInfo?.icon && <catInfo.icon className="w-5 h-5 text-lws-green" />}
                      {catInfo?.label || category}
                      <span className="text-sm font-normal text-stone-400">({categoryResources.length})</span>
                    </h2>
                    <div className="space-y-3">
                      {categoryResources.map(resource => (
                        <ResourceCard 
                          key={resource.id} 
                          resource={resource} 
                          onPlay={setPlayingVideo}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Show flat list for single category
            <div className="space-y-3">
              {filteredResources.map(resource => (
                <ResourceCard 
                  key={resource.id} 
                  resource={resource} 
                  onPlay={setPlayingVideo}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Player Modal */}
      {playingVideo && (
        <VideoPlayerModal
          resource={playingVideo}
          onClose={() => setPlayingVideo(null)}
        />
      )}
    </div>
  );
}
